import groupBy from 'lodash/groupBy';

import {
  Menu,
  MenuItemGroup,
  BuilderMode,
  BuilderModeKind,
  MenuItemCategory,
  MenuItem,
  SingleEntreeTypeBuilderMode,
  MenuEntree,
  SelectableVariantMenuItem,
  PortionItemType,
  SelectableMenuItem,
  SelectableMenuItemSource,
  ItemCardCustomization,
  ItemCardCustomizationType,
  ItemCardSelectionInfoKind,
  ZeroVariantBuilderMode,
  MenuItemCardCustomization,
  ItemCardSelectionInfo,
  MenuMetadataForChannel,
  MultiselectSingleEntreeTypeBuilderMode,
  MealBuilderItemSection,
  GroupedMenuItems,
  GroupedItemSelectionOption,
  MenuItemMetadata,
  ItemCustomization,
} from '@/models';
import { findItemInMenu, isDefined, getItemIds } from '@/helpers';
import { findReferencingMenuItems, getPortionMenuItems, getSelectablePortionItems } from '../utils';
import { Logger } from '@/services/logger/logger';
import { MetadataService } from '@/services/metadata/metadata.service';
import { UtilsService } from '@/services/utils/utils.service';
import { MenuService } from '@/services/menu/menu.service';
import reduce from 'lodash/reduce';
import map from 'lodash/map';
import isEqual from 'lodash/isEqual';
import every from 'lodash/every';
import isEmpty from 'lodash/isEmpty';

export interface MealBuilderModeMapper {
  getBuilderMode(menu: Menu, metadata: MenuMetadataForChannel, group: MenuItemGroup): BuilderMode;
}

export class AppMealBuilderModeMapper implements MealBuilderModeMapper {
  constructor(private metadata: MetadataService, private utils: UtilsService, private menu: MenuService, private logger: Logger) {}

  getBuilderMode(menu: Menu, metadata: MenuMetadataForChannel, group: MenuItemGroup): BuilderMode {
    const items = this.metadata
      .resolveItemIdsInGroup(group, metadata)
      .map(i => findItemInMenu(i, menu))
      .filter(isDefined);

    const itemCategoryLookup = groupBy(items, i => i.itemCategory);
    const categories = Object.keys(itemCategoryLookup);
    const itemTypes = Object.keys(groupBy(items, i => i.itemType));
    const variantItemTypes = this.getVariantItemTypesInMenu(menu);

    // If this has no variant types, this is a zero-variant builder
    if (!items.find(i => !!variantItemTypes.find(t => i.itemType == t))) {
      return this.convertMenuItemsIntoZeroVariantBuilderMode(menu, metadata, group);
    }

    const numItemTypes = itemTypes.length;
    const category = categories[0];

    // If there's only one type, this is a builder for a single item type
    if (category == MenuItemCategory.Entree && numItemTypes == 1) {
      const maxItemQuantity = items[0].maxQuantity;

      // If the max quantity of any one item is > 1, this is a multiselect mode; otherwise it's a single select mode
      return maxItemQuantity && maxItemQuantity > 1
        ? this.convertMenuItemsIntoMultiselectSingleEntreeTypeBuilderMode(items, menu, metadata, group)
        : this.convertMenuItemsIntoSingleEntreeTypeBuilderMode(items, menu, metadata, group);
    }

    this.logger.error('Unknown builder mode!');
    throw new Error('Unknown builder mode!');
  }

  convertMenuItemsIntoSingleEntreeTypeBuilderMode(
    items: readonly MenuItem[],
    menu: Menu,
    metadata: MenuMetadataForChannel,
    group: MenuItemGroup,
  ): SingleEntreeTypeBuilderMode {
    const itemTypeLookup = groupBy(items, i => i.itemType);

    const types = Object.keys(itemTypeLookup).map(typeName => {
      const itemVariants = itemTypeLookup[typeName] as MenuEntree[];

      // Groups items by their categories (so, conceptually this would be "the item categories for a given variant, as well as the entree itself")
      // in the case of an entree, for example, the variant would mean what filling we're working with
      const variants = itemVariants.map<SelectableVariantMenuItem>(variant => {
        const contents = variant.contents || [];
        const referencingContents = findReferencingMenuItems(variant.contents || [], variant.itemId);
        const portions = getPortionMenuItems(referencingContents, PortionItemType.ExtraPortion);

        const selectableContents = map(this.groupMenuItemsByGroupName(contents, metadata), (groupedItem, groupName) =>
          this.groupedMenuItemToSelectableItem(groupedItem, groupName, SelectableMenuItemSource.VariantContents, metadata),
        );

        const variantCustomizations = portions.map<ItemCardCustomization>(i => {
          const selectableItem = selectableContents.find(c => c.item.itemId == i.itemId) as SelectableMenuItem;
          if (!selectableItem) {
            this.logger.unreachable('Tried to find selectable item in selectableContents and failed', {
              item: i.itemId,
              selectableContents: getItemIds(selectableContents),
            });
          }

          return {
            type: ItemCardCustomizationType.SelectableItem,
            text: `Double +${this.utils.formatPrice(this.menu.getItemPrice(i))}`,
            iconText: '2x',
            selected: false,
            item: selectableItem,
            portion: i.itemType as PortionItemType,
            hidden: false,
          };
        });

        return {
          item: variant,
          itemSource: SelectableMenuItemSource.Variant,
          customizations: variantCustomizations,
          portions: getSelectablePortionItems(selectableContents, 'all'),
          selectionInfo: { kind: ItemCardSelectionInfoKind.Single, selected: false },
          contents: selectableContents,
          message: null,
        };
      });

      return {
        type: typeName,
        fillings: variants,
      };
    });

    if (types.length > 1) {
      const error = new Error('Found more than one type for builder -- this is not implemented!');

      this.logger.error('Error while getting builder mode info', undefined, error);
      throw error;
    }

    // TODO-3hbdjn: this method technically supports more than one type/variant, but to keep it simple
    // I'm limiting it to just a single entree type (but keeping the mapping around for when we need it)
    const selectedType = types[0];
    const drinks = map(this.groupMenuItemsByGroupName(menu.drinks, metadata), (groupedItem, groupName) =>
      this.groupedMenuItemToSelectableItem(groupedItem, groupName, SelectableMenuItemSource.NonVariant, metadata),
    );

    const result: BuilderMode = {
      mode: BuilderModeKind.SingleEntreeType,
      group,
      entree: selectedType,
      sides: (menu.sides ?? []).map(i => this.menuItemToSelectableItem(i, SelectableMenuItemSource.NonVariant, metadata)),
      drinks,
    };

    this.logger.debug('Meal Builder Mode result', { mode: result.mode, entreeType: selectedType.type });

    return result;
  }

  // We don't currently have a scenario where this function is called
  convertMenuItemsIntoMultiselectSingleEntreeTypeBuilderMode(
    items: readonly MenuItem[],
    menu: Menu,
    metadata: MenuMetadataForChannel,
    group: MenuItemGroup,
  ): MultiselectSingleEntreeTypeBuilderMode {
    const itemTypeLookup = groupBy(items, i => i.itemType);

    const types = Object.keys(itemTypeLookup).map(typeName => {
      const itemVariants = itemTypeLookup[typeName] as MenuEntree[];

      const variants = itemVariants.map<SelectableVariantMenuItem>(variant => {
        const contents = variant.contents || [];

        const selectableContents = contents.map<SelectableMenuItem>(item => this.menuItemToSelectableItem(item, SelectableMenuItemSource.VariantContents, metadata));

        return {
          contents: selectableContents,
          customizations: [],
          item: variant,
          itemSource: SelectableMenuItemSource.Variant,
          portions: [],
          message: null,
          selectionInfo: { kind: ItemCardSelectionInfoKind.Quantity, quantity: 0, maxQuantity: variant.maxQuantity || 0 },
        };
      });

      const virtualItemSection: MealBuilderItemSection = {
        id: '',
        title: '',
        hidden: true,
        showSelectNone: false,
        minQuantity: null,
        maxQuantity: (variants[0] && variants[0].item.maxQuantity) || null,
        items: variants,
        maxCustomizations: null,
      };

      return {
        type: typeName,
        variants,
        virtualItemSection,
      };
    });

    if (types.length > 1) {
      const error = new Error('Found more than one type for builder -- this is not implemented!');

      this.logger.error('Error while getting builder mode info', undefined, error);
      throw error;
    }

    const drinks = map(this.groupMenuItemsByGroupName(menu.drinks, metadata), (groupedItem, groupName) =>
      this.groupedMenuItemToSelectableItem(groupedItem, groupName, SelectableMenuItemSource.NonVariant, metadata),
    );

    const entree = types[0];
    const result: MultiselectSingleEntreeTypeBuilderMode = {
      mode: BuilderModeKind.MultiselectSingleEntreeType,
      group,
      entree,
      sides: (menu.sides ?? []).map(i => this.menuItemToSelectableItem(i, SelectableMenuItemSource.NonVariant, metadata)),
      drinks,
    };

    this.logger.debug('Meal Builder Mode result', { mode: result.mode, entreeType: entree.type });

    return result;
  }

  convertMenuItemsIntoZeroVariantBuilderMode(menu: Menu, metadata: MenuMetadataForChannel, group: MenuItemGroup): ZeroVariantBuilderMode {
    const itemsMap: { [key: string]: MenuItem } = {};
    menu.sides!.forEach(i => (itemsMap[i.itemId] = i));
    menu.drinks!.forEach(i => (itemsMap[i.itemId] = i));

    const itemCategoryLookup: { [key: string]: MenuItem[] } = {};
    group.itemSections
      .map(sectionName => metadata.itemSections[sectionName])
      .forEach(section => {
        const items = section!.items.map(i => itemsMap[i.menuItemId]).filter(i => !!i);
        if (items.length > 0) {
          itemCategoryLookup[section!.name] = items;
        }
      });

    return {
      mode: BuilderModeKind.ZeroVariant,
      group,
      categories: Object.keys(itemCategoryLookup).reduce((acc, category) => {
        const categoryItems = itemCategoryLookup[category];
        acc[category] = map(this.groupMenuItemsByGroupName(categoryItems, metadata), (groupedItem, groupName) =>
          this.groupedMenuItemToSelectableItem(groupedItem, groupName, SelectableMenuItemSource.NonVariant, metadata),
        );
        return acc;
      }, {} as any),
    };
  }

  private groupMenuItemsByGroupName(items: MenuItem[] | null | undefined, metadata: MenuMetadataForChannel): GroupedMenuItems | {} {
    if (!items) {
      return {};
    }

    // TODO Tech debt - cache this per metadata grab
    const defaultItemIdsForGroupedItems = map(metadata.itemGroups, itemGroup => itemGroup.defaultItemId);

    return reduce(
      items,
      (accu: GroupedMenuItems, item) => {
        const itemMetadata = metadata.items[item.itemId];
        if (itemMetadata?.groupName) {
          if (accu[itemMetadata.groupName]) {
            accu[itemMetadata.groupName].push(item);
          } else {
            accu[itemMetadata.groupName] = [item];
          }
        } else if (defaultItemIdsForGroupedItems.includes(item.itemId)) {
          // Account for items that are default item Ids but don't have groupnames attached e.g. CMG-1040 cauli rice has multiple groups
          const groupNames = reduce(
            metadata.itemGroups,
            (matchedGroupNames, itemGroup, groupName) => (itemGroup.defaultItemId === item.itemId ? [...matchedGroupNames, groupName] : matchedGroupNames),
            [] as string[],
          );
          // Add item to all groups in which it is a default item
          for (const groupName of groupNames) {
            if (accu[groupName]) {
              accu[groupName].push(item);
            } else {
              accu[groupName] = [item];
            }
          }
        } else {
          accu[item.itemId] = [item];
        }
        return accu;
      },
      {},
    );
  }

  // groupedItem will mostly be a single menu item, an array of two for things like fountain drinks, and no limit for abominations such as cauli rice
  private groupedMenuItemToSelectableItem(groupedItem: MenuItem[], groupName: string, source: SelectableMenuItemSource, metadata: MenuMetadataForChannel): SelectableMenuItem {
    // Universal menu makes a pass through here, and item availability is not a concept for universal menu
    // Only check for item avaiability if its defined on the menu item to avoid filtering out items for universal menu
    // If all items in the group are unavailable, we want to keep them and show them with the unavailable treatment on the UI
    if(groupedItem.filter(gi => gi.isItemAvailable === undefined || gi.isItemAvailable).length !== 0) {
      groupedItem = groupedItem.filter(gi => gi.isItemAvailable === undefined || gi.isItemAvailable);
    }

    if (groupedItem.length === 1) {
      return this.menuItemToSelectableItem(groupedItem[0], source, metadata);
    }

    const selectionInfo = groupedItem.map(item => {
      const info = this.getDefaultSelectionInfoValueForItemType(item);
      if (!info) {
        throw new Error(`Failed to resolve selection type for item '${item.itemId}'`);
      }
      return info;
    });

    if (!every(selectionInfo, info => isEqual(info, selectionInfo[0]))) {
      this.logger.error(`FATAL: Selection info is not equal across grouped items. Validate menu metadata max quantity for ${map(groupedItem, 'itemId')}`);
    }

    let genericCustomizations: ItemCardCustomization[] = [];

    const groupedItemSelectionOptions = groupedItem.map(item => {
      const metadataItem = metadata.items[item.itemId];
      if (isEmpty(genericCustomizations)) {
        // Keep any mixed customizations, e.g. Cauli rice has a normal CMG-ID, extra CMG-ID, but standard customization for side
        genericCustomizations = this.processNonGroupedItemCustomizations(metadataItem, item.customizations);
      }

      return {
        id: item.itemId,
        name: item.itemName,
        price: item.unitPrice,
        deliveryPrice: item.unitDeliveryPrice,
        nutrition: metadataItem?.nutrition ?? [],
        posId: item.posId,
        showCustomizationPrice: metadataItem?.showCustomizationPrice,
        asCustomizationName: metadataItem?.asCustomizationName,
        showCustomizationNameWhenSelected: metadataItem?.showCustomizationNameWhenSelected,
        countTowardsContentMax: item.countTowardsContentMax ?? 0,
        countTowardsCustomizationMax: item.countTowardsCustomizationMax ?? 0,
      } as GroupedItemSelectionOption;
    });

    const defaultItemId = metadata.itemGroups[groupName].defaultItemId;
    const defaultItem = groupedItem.find(singleItem => singleItem.itemId === defaultItemId) ?? groupedItem[0];

    return {
      item: defaultItem, // Leverage item field for things like maxUnitCount
      itemSource: source,
      groupedItemSelectionOptions,
      customizations: genericCustomizations,
      selectionInfo: selectionInfo[0],
      displayNameForGroupedItems: metadata.itemGroups[groupName]?.displayName,
      groupedItemsCalorieRange: metadata.itemGroups[groupName].calories,
      displayRangeFormatForGroupedItems: metadata.itemGroups[groupName]?.displayRangeFormat ?? false,
      customizeTextForGroupedItems: metadata.itemGroups[groupName]?.customizeText ?? 'Customize',
      defaultItemId,
      message: null,
    };
  }

  private processNonGroupedItemCustomizations(itemMetadata?: MenuItemMetadata, itemCustomizations?: ItemCustomization[] | null) {
    return (itemCustomizations ?? [])
      .filter(isDefined)
      .map<MenuItemCardCustomization>(c => {
        const customizationMetadata = itemMetadata?.customizations?.find(cu => cu.customizationId === c.id);

        return {
          type: ItemCardCustomizationType.Customization,
          text: customizationMetadata?.alias ?? c.name,
          iconText: null,
          selected: false,
          customization: c,
          hidden: customizationMetadata?.isHidden ?? false,
        };
      })
      .sort((l, r) => (l.customization.id < r.customization.id ? -1 : l.customization.id === r.customization.id ? 0 : 1));
  }

  private menuItemToSelectableItem(item: MenuItem, source: SelectableMenuItemSource, metadata: MenuMetadataForChannel): SelectableMenuItem {
    const selectionInfo = this.getDefaultSelectionInfoValueForItemType(item);
    if (!selectionInfo) {
      throw new Error(`Failed to resolve selection type for item '${item.itemId}'`);
    }

    const itemMetadata = metadata.items[item.itemId];

    return {
      item,
      itemSource: source,
      customizations: this.processNonGroupedItemCustomizations(itemMetadata, item.customizations),
      selectionInfo,
      message: null,
    };
  }

  private getDefaultSelectionInfoValueForItemType(item: MenuItem): ItemCardSelectionInfo {
    return item.maxQuantity && item.maxQuantity > 1
      ? { kind: ItemCardSelectionInfoKind.Quantity, quantity: 0, maxQuantity: item.maxQuantity }
      : { kind: ItemCardSelectionInfoKind.Single, selected: false };
  }

  private getVariantItemTypesInMenu(menu: Menu): string[] {
    // Right now we just pull variants from entrees
    const variantCategories = [...(menu.entrees || [])];

    return Object.keys(groupBy(variantCategories, i => i.itemType));
  }
}
